import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import {
  type ApiError,
  type ApiResponse,
  addActionNameToMutationResult,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

const DeleteReasonVariablesSchema = z.object({
  id: z.union([z.string(), z.number()]),
});

export type DeleteReasonVariables = z.infer<typeof DeleteReasonVariablesSchema>;

export async function deleteReasonFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: DeleteReasonVariables,
) {
  const parsed = DeleteReasonVariablesSchema.parse(variables);
  const instance = await axiosInstance;
  return instance.delete(`/reasons/${parsed.id}`);
}

export function useDeleteReason() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, DeleteReasonVariables>({
    mutationFn: variables => deleteReasonFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['reasons'] });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    DeleteReasonVariables,
    'deleteReason'
  >('deleteReason', mutation);
}
